/**
 * Card UI helpers — pure logic consumed from `@warcarp/api-types/gameLogic`
 * (the single source of truth shared with the engine).
 *
 *  - `isValidSet`     — does a chosen trio form a legal turn-in?
 *  - `hasValidSet`    — does any 3-card subset of a hand form a valid set?
 *  - `nextCardBonus`  — preview the bonus armies the next turn-in awards.
 *  - `cardImagery`    — WarCarp-themed emoji + flavor label per card type.
 */
import type { Card, CardType, CardBonusSchedule } from '@warcarp/api-types'
import { isValidSet as sharedIsValidSet, scheduleBonus } from '@warcarp/api-types'

/**
 * Validate that exactly 3 cards form a legal turn-in set.
 * Delegates to the shared authoritative implementation in `@warcarp/api-types`.
 */
export function isValidSet(cards: readonly Card[]): boolean {
  return sharedIsValidSet(cards)
}

/**
 * True if any 3-card subset of `hand` forms a valid set. Used to drive the
 * placement-phase card-icon glow when the player has at least one tradeable
 * set in hand. With up to 5 cards in hand the brute force C(5,3) = 10
 * combinations is negligible — no need for a smarter check.
 */
export function hasValidSet(hand: readonly Card[]): boolean {
  if (hand.length < 3) return false
  for (let i = 0; i < hand.length - 2; i++) {
    for (let j = i + 1; j < hand.length - 1; j++) {
      for (let k = j + 1; k < hand.length; k++) {
        const trio = [hand[i] as Card, hand[j] as Card, hand[k] as Card]
        if (isValidSet(trio)) return true
      }
    }
  }
  return false
}

/**
 * After a turn-in, should the modal auto-close itself? Only when the player is
 * NOT still forced to trade (mustTurnIn) AND no further valid set remains in
 * the hand (fewer than 3 cards, or no valid combination) — i.e. there's nothing
 * left to do, so don't leave them staring at a dead-end hand.
 */
export function shouldAutoCloseTurnIn(hand: readonly Card[], mustTurnIn: boolean): boolean {
  return !mustTurnIn && !hasValidSet(hand)
}

/** Always-visible guidance below the hand, independent of optional inline tips. */
export function cardTurnInStatus(hand: readonly Card[], selected: readonly Card[], canTurnIn: boolean): string {
  if (hand.length < 3) return 'Collect at least 3 cards to make a set.'
  if (!hasValidSet(hand)) return 'No valid set yet — collect more cards.'
  if (!canTurnIn) return 'You have a set. Turn it in during placement.'
  if (selected.length === 0) return 'Select a 3 card set to turn in.'
  if (selected.length < 3) {
    const remaining = 3 - selected.length
    return `Select ${remaining}${selected.length ? ' more' : ''} card${remaining === 1 ? '' : 's'} to turn in.`
  }
  if (!isValidSet(selected)) return 'Not a valid set — use 3 of a kind or 1 of each.'
  return 'Valid set! Tap Turn in to receive your armies.'
}

interface TurnInResultProjection {
  cards?: Record<string, readonly Card[]>
  mustTurnInCards?: boolean
}

/**
 * Decide from the authoritative turn-in response whether the cards modal has
 * any work left. Reading the mutation result avoids racing the React Query
 * cache update against component effects that still hold the pre-trade hand.
 */
export function shouldCloseAfterTurnIn(
  state: unknown,
  playerId: string,
): boolean {
  const projection =
    state && typeof state === 'object'
      ? (state as TurnInResultProjection)
      : {}
  return shouldAutoCloseTurnIn(
    projection.cards?.[playerId] ?? [],
    projection.mustTurnInCards === true,
  )
}

/**
 * Preview the bonus armies the NEXT turn-in will award, given how many
 * have already happened across the whole game (`state.cardTurnIns`).
 *
 *  - `cycle`        → [4, 6, 8, 10][turnInsSoFar % 4]
 *  - `incremental`  → 4 + turnInsSoFar
 *
 * Thin adapter over the shared `scheduleBonus` from `@warcarp/api-types`.
 * The shared function takes `(cardTurnIns, schedule)` — note the argument
 * order is flipped compared to this local signature `(schedule, turnInsSoFar)`.
 * This wrapper preserves the existing call-site API to avoid churn.
 */
export function nextCardBonus(
  schedule: CardBonusSchedule,
  turnInsSoFar: number,
): number {
  return scheduleBonus(turnInsSoFar, schedule)
}

/**
 * Per-type WarCarp-themed imagery. Identifiers stay aligned with the
 * engine's `CardType`; we only theme the rendering.
 */
export interface CardImagery {
  emoji: string
  label: string
  /** A semantic NativeWind text-color token. Used for the type label tint. */
  accentColor: string
  /**
   * Nudge the emoji down by this fraction of its size so its eye lines up with
   * the other card fish. The shark glyph draws its eye high (and its top was
   * clipped on native), so it sits lower than the rest.
   */
  emojiDrop?: number
}

export function cardImagery(type: CardType): CardImagery {
  switch (type) {
    case 'infantry':
      return { emoji: '🦈', label: 'Blitzfin', accentColor: 'text-ink', emojiDrop: 0.14 }
    case 'cavalry':
      return { emoji: '🐟', label: 'Warfish', accentColor: 'text-ink' }
    case 'artillery':
      return { emoji: '🐡', label: 'Riptide', accentColor: 'text-ink' }
    case 'wild':
      return { emoji: '🦑', label: 'Kraken', accentColor: 'text-blood' }
  }
}
